import React, { useState, useEffect } from 'react';

/**
 * NumberInput – Tailwind styled, theme-aware (#583ca3)
 * Props:
 *  - value: number (controlled)
 *  - onChange: (n: number) => void
 *  - min?: number (default 0)
 *  - max?: number (default 100)
 *  - step?: number (default 1)
 *  - label?: string | null
 *  - unit?: string | null  // e.g., "cm", "kg". Shown to the right of the input box
 *  - decimals?: number | null  // Number of decimal places to display. Null for unlimited
 */
const NumberInput = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  label,
  unit,
  decimals = null
}) => {
  const formatValue = (val) => {
    if (val === null || !Number.isFinite(val)) return '';
    return decimals !== null ? val.toFixed(decimals) : String(val);
  };

  // Local state to hold the string value of the input
  const [inputValue, setInputValue] = useState(formatValue(value));

  // Sync local state when the controlled `value` prop changes from the parent
  useEffect(() => {
    // Avoid updating if the user is actively typing a valid partial number (e.g., "-")
    const isUserTypingPartial = inputValue === '-' && (value === null || value === 0);
    if (!isUserTypingPartial) {
      setInputValue(formatValue(value));
    }
  }, [value, decimals]);

  const clamp = (n) => Math.min(max, Math.max(min, n));

  const handleChange = (e) => {
    const currentString = e.target.value;
    setInputValue(currentString);

    // Allow empty input or just a negative sign
    if (currentString === '' || currentString === '-') {
      onChange(null); // Notify parent that the number is cleared
      return;
    }

    const num = parseFloat(currentString);
    if (!Number.isNaN(num)) {
      // We don't clamp here to allow user to type numbers out of range
      // The value will be clamped on blur
      onChange(num);
    }
  };
  
  const handleBlur = () => {
    const num = parseFloat(inputValue);
    if (!Number.isNaN(num)) {
      const clampedValue = clamp(num);
      onChange(clampedValue);
      setInputValue(formatValue(clampedValue));
    } else {
      // If input is invalid on blur (e.g., empty or "-"), reset to min or last valid value
      // Here, we reset to min value if the original value was null/invalid
      const resetValue = value === null ? min : clamp(value);
      onChange(resetValue);
      setInputValue(formatValue(resetValue));
    }
  };

  const handleStep = (direction) => {
    const currentValue = parseFloat(inputValue);
    const startValue = !Number.isNaN(currentValue) ? currentValue : 0;
    const stepValue = direction === 'inc' ? step : -step;
    
    // Using parseFloat and toFixed to handle floating point inaccuracies
    const sum = Number(startValue) + Number(stepValue);
    const nextValue = parseFloat(sum.toFixed(6));
    
    const clampedValue = clamp(nextValue);
    onChange(clampedValue);
    setInputValue(formatValue(clampedValue));
  };
  
  const canDec = typeof value === 'number' && value > min;
  const canInc = typeof value === 'number' && value < max;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden h-full">
  <div className="w-full h-full flex justify-between items-center gap-x-1">
    {/* Left: label + input */}
    <div className={label ? "grow h-full px-3 flex items-center" : "grow h-full px-3 flex items-center"}>
      {label && (
        <span className="mr-2 text-xs text-gray-500 whitespace-nowrap">
          {label}
        </span>
      )}

      <div className="flex items-center w-full h-full">
        <input
          className={`w-full h-full p-0 bg-transparent border-0 text-center ${disabled ? 'text-gray-400' : 'text-gray-800'} leading-none outline-none focus:outline-none focus-visible:outline-none 
          focus:ring-0 focus:shadow-none focus:border-transparent
          appearance-none
          [&::-webkit-inner-spin-button]:appearance-none
          [&::-webkit-outer-spin-button]:appearance-none
          [&::-webkit-inner-spin-button]:m-0
          [&::-webkit-outer-spin-button]:m-0`}
          style={{ MozAppearance: 'textfield' }}
          type="text"
          inputMode="decimal"
          aria-roledescription="Number field"
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {unit && (
          <span className="ml-1 select-none text-[10px] text-gray-500">
            {unit}
          </span>
        )}
      </div>
    </div>

    {/* Right: controls */}
    <div className="h-full w-10 flex flex-col border-s border-gray-200 divide-y divide-gray-200 min-h-0">
      <button
        type="button"
        aria-label="Decrease"
        disabled={!canDec || disabled}
        onClick={() => handleStep('dec')}
        className="flex-1 min-h-0 p-0 inline-flex justify-center items-center text-xs bg-transparent text-[#583ca3] cursor-pointer hover:bg-[#583ca3]/10 focus:outline-none disabled:opacity-40 disabled:pointer-events-none"
      >
        <svg className="w-3 h-3 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14" />
        </svg>
      </button>
      <button
        type="button"
        aria-label="Increase"
        disabled={!canInc || disabled}
        onClick={() => handleStep('inc')}
        className="flex-1 min-h-0 p-0 inline-flex justify-center items-center text-xs bg-transparent text-[#583ca3] cursor-pointer hover:bg-[#583ca3]/10 focus:outline-none disabled:opacity-40 disabled:pointer-events-none"
      >
        <svg className="w-3 h-3 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14" />
          <path d="M12 5v14" />
        </svg>
      </button>
    </div>
  </div>
</div>
  );
};

export default NumberInput;